import { Component, OnInit, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { ClientHistoryService } from '../service/client-history.service';
import { FormsModule } from '@angular/forms';
import { CommonModule, TitleCasePipe } from '@angular/common';

@Component({
  selector: 'app-client-history',
  templateUrl: './client-history.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TitleCasePipe
  ],
  styleUrls: ['./client-history.component.css']
})
export class ClientHistoryComponent implements OnInit {

  // ─── Identity ─────────────────────────────────────────────────────────────
  clientId: string = '';

  // ─── Summary ──────────────────────────────────────────────────────────────
  client: any = null;
  summary: any = null;
  summaryLoading: boolean = false;

  // ─── Tabs ─────────────────────────────────────────────────────────────────
  //
  // Only one tab is on screen at a time, so the toolbar, pagination and menu
  // handlers all work against `state` - the active tab's slice - instead of
  // being written out once per tab.
  activeTab: string = 'appointments';

  appt: any = {
    rows: [],
    loading: false,
    loaded: false,
    pagination: { page: 1, limit: 10, total: 0, total_pages: 0 },
    startDate: '',
    endDate: '',
    draftStart: '',
    draftEnd: '',
    status: '',
    mode: '',
    paymentStatus: '',
    draftMode: '',
    draftPaymentStatus: ''
  };

  pay: any = {
    rows: [],
    loading: false,
    loaded: false,
    pagination: { page: 1, limit: 10, total: 0, total_pages: 0 },
    startDate: '',
    endDate: '',
    draftStart: '',
    draftEnd: '',
    status: '',
    method: '',
    search: '',
    draftSearch: ''
  };

  ref: any = {
    rows: [],
    loading: false,
    loaded: false,
    pagination: { page: 1, limit: 10, total: 0, total_pages: 0 },
    startDate: '',
    endDate: '',
    draftStart: '',
    draftEnd: '',
    status: '',
    refundType: '',
    search: '',
    draftRefundType: '',
    draftSearch: ''
  };

  act: any = {
    rows: [],
    loading: false,
    loaded: false,
    pagination: { page: 1, limit: 10, total: 0, total_pages: 0 },
    // Load More appends pages, so the range label counts from the first page
    // still on screen rather than from the current one.
    firstPage: 1,
    startDate: '',
    endDate: '',
    draftStart: '',
    draftEnd: '',
    status: ''
  };

  private readonly slices: any = {
    appointments: 'appt',
    payments: 'pay',
    refunds: 'ref',
    activity: 'act'
  };

  get state(): any {
    return (this as any)[this.slices[this.activeTab] || 'appt'];
  }

  // ─── Filter options ───────────────────────────────────────────────────────

  // A confirmed appointment is never shown as "confirmed" - the clock decides
  // between upcoming, ongoing and completed. Unpaid and abandoned bookings are
  // excluded by the API, so they are not offered as filters.
  statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'completed', label: 'Completed' },
    { value: 'ongoing', label: 'Ongoing' },
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  consultationModeOptions = [
    { value: '', label: 'All Modes' },
    { value: 'video_call', label: 'Video Call' },
    { value: 'phone_call', label: 'Phone Call' },
    { value: 'online', label: 'Online' },
    { value: 'in_person', label: 'In Person' }
  ];

  paymentStatusOptions = [
    { value: '', label: 'All Payments' },
    { value: 'paid', label: 'Paid' },
    { value: 'pending', label: 'Pending' },
    { value: 'failed', label: 'Failed' },
    { value: 'refunded', label: 'Refunded' }
  ];

  // Order status, which is what the payments table shows.
  orderStatusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'paid', label: 'Paid' },
    { value: 'created', label: 'Created' },
    { value: 'failed', label: 'Failed' },
    { value: 'refunded', label: 'Refunded' }
  ];

  methodOptions = [
    { value: '', label: 'All Methods' },
    { value: 'upi', label: 'UPI' },
    { value: 'card', label: 'Card' },
    { value: 'netbanking', label: 'Netbanking' },
    { value: 'wallet', label: 'Wallet' },
    { value: 'emi', label: 'EMI' }
  ];

  refundStatusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'processed', label: 'Completed' },
    { value: 'created', label: 'Initiated' },
    { value: 'failed', label: 'Failed' }
  ];

  refundTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'full', label: 'Full Refund' },
    { value: 'partial', label: 'Partial Refund' }
  ];

  eventTypeOptions = [
    { value: '', label: 'All Events' },
    { value: 'appointment_booked', label: 'Appointment Booked' },
    { value: 'appointment_confirmed', label: 'Appointment Confirmed' },
    { value: 'appointment_completed', label: 'Appointment Completed' },
    { value: 'appointment_cancelled', label: 'Appointment Cancelled' },
    { value: 'payment_initiated', label: 'Payment Initiated' },
    { value: 'payment_successful', label: 'Payment Successful' },
    { value: 'payment_failed', label: 'Payment Failed' },
    { value: 'refund_initiated', label: 'Refund Initiated' },
    { value: 'refund_processed', label: 'Refund Processed' }
  ];

  pageSizeOptions = [10, 25, 50, 100];

  // ─── Toolbar menus ────────────────────────────────────────────────────────
  showDateMenu: boolean = false;
  showFilterMenu: boolean = false;

  // ─── Detail panel ─────────────────────────────────────────────────────────
  //
  // One panel serves every tab; `detailKind` says which body to render.
  detailKind: string | null = null;
  detailId: string | null = null;
  detail: any = null;
  detailLoading: boolean = false;

  exporting: boolean = false;

  private readonly statusLabels: any = {
    pending_payment: 'Payment Pending',
    cancellation_pending: 'Cancellation Pending',
    no_show: 'No Show',
    resheduled: 'Rescheduled'
  };

  private readonly modeLabels: any = {
    video_call: 'Video Call',
    phone_call: 'Phone Call',
    in_person: 'In Person',
    online: 'Online',
    chat: 'Chat',
    upi: 'UPI',
    emi: 'EMI',
    netbanking: 'Netbanking'
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private clientHistoryService: ClientHistoryService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.clientId = this.route.snapshot.paramMap.get('clientId') || '';

    if (!this.clientId) {
      this.router.navigate(['/admin/lexiz-clients']);
      return;
    }

    this.loadSummary();
    this.loadAppointments();
  }

  // Close the toolbar popovers on any outside click.
  @HostListener('document:click')
  closeMenus(): void {
    this.showDateMenu = false;
    this.showFilterMenu = false;
  }

  // ═══════════════════════════════════════════════════════════
  //  API
  // ═══════════════════════════════════════════════════════════

  loadSummary(): void {
    this.summaryLoading = true;

    this.clientHistoryService.getSummary(this.clientId).subscribe({
      next: (resp: any) => {
        this.summary = resp?.['data'] || null;
        this.client = this.summary?.['client'] || null;
        this.summaryLoading = false;
      },
      error: (err: any) => {
        this.summaryLoading = false;
        this.toastr.error(
          err?.error?.msg || 'Unable to load client summary'
        );
      }
    });
  }

  private appointmentParams(): any {
    return {
      page: this.appt.pagination.page,
      limit: this.appt.pagination.limit,
      status: this.appt.status,
      consultation_mode: this.appt.mode,
      payment_status: this.appt.paymentStatus,
      start_date: this.appt.startDate,
      end_date: this.appt.endDate
    };
  }

  private paymentParams(): any {
    return {
      page: this.pay.pagination.page,
      limit: this.pay.pagination.limit,
      status: this.pay.status,
      method: this.pay.method,
      search: this.pay.search,
      start_date: this.pay.startDate,
      end_date: this.pay.endDate
    };
  }

  private refundParams(): any {
    return {
      page: this.ref.pagination.page,
      limit: this.ref.pagination.limit,
      status: this.ref.status,
      refund_type: this.ref.refundType,
      search: this.ref.search,
      start_date: this.ref.startDate,
      end_date: this.ref.endDate
    };
  }

  private activityParams(): any {
    return {
      page: this.act.pagination.page,
      limit: this.act.pagination.limit,
      type: this.act.status,
      start_date: this.act.startDate,
      end_date: this.act.endDate
    };
  }

  loadAppointments(): void {
    this.appt.loading = true;

    this.clientHistoryService
      .getAppointments(this.clientId, this.appointmentParams())
      .subscribe({
        next: (resp: any) => {
          this.appt.rows = resp?.['data'] || [];
          this.appt.pagination = resp?.['pagination'] || this.appt.pagination;
          this.appt.loading = false;
          this.appt.loaded = true;
        },
        error: (err: any) => {
          this.appt.rows = [];
          this.appt.loading = false;
          this.appt.loaded = true;
          this.toastr.error(
            err?.error?.msg || 'Unable to load appointment history'
          );
        }
      });
  }

  loadPayments(): void {
    this.pay.loading = true;

    this.clientHistoryService
      .getPayments(this.clientId, this.paymentParams())
      .subscribe({
        next: (resp: any) => {
          this.pay.rows = resp?.['data'] || [];
          this.pay.pagination = resp?.['pagination'] || this.pay.pagination;
          this.pay.loading = false;
          this.pay.loaded = true;
        },
        error: (err: any) => {
          this.pay.rows = [];
          this.pay.loading = false;
          this.pay.loaded = true;
          this.toastr.error(
            err?.error?.msg || 'Unable to load payment history'
          );
        }
      });
  }

  loadRefunds(): void {
    this.ref.loading = true;

    this.clientHistoryService
      .getRefunds(this.clientId, this.refundParams())
      .subscribe({
        next: (resp: any) => {
          this.ref.rows = resp?.['data'] || [];
          this.ref.pagination = resp?.['pagination'] || this.ref.pagination;
          this.ref.loading = false;
          this.ref.loaded = true;
        },
        error: (err: any) => {
          this.ref.rows = [];
          this.ref.loading = false;
          this.ref.loaded = true;
          this.toastr.error(
            err?.error?.msg || 'Unable to load refund history'
          );
        }
      });
  }

  // `append` is Load More: the next page is added to what is on screen
  // instead of replacing it.
  loadActivity(append: boolean = false): void {
    this.act.loading = !append;
    this.act.loadingMore = append;

    if (append) {
      this.act.pagination.page = this.act.pagination.page + 1;
    } else {
      this.act.firstPage = this.act.pagination.page;
    }

    this.clientHistoryService
      .getActivity(this.clientId, this.activityParams())
      .subscribe({
        next: (resp: any) => {
          const rows = resp?.['data'] || [];

          this.act.rows = append ? [...this.act.rows, ...rows] : rows;
          this.act.pagination = resp?.['pagination'] || this.act.pagination;
          this.act.loading = false;
          this.act.loadingMore = false;
          this.act.loaded = true;
        },
        error: (err: any) => {
          if (!append) {
            this.act.rows = [];
          }

          this.act.loading = false;
          this.act.loadingMore = false;
          this.act.loaded = true;
          this.toastr.error(
            err?.error?.msg || 'Unable to load the activity timeline'
          );
        }
      });
  }

  get canLoadMore(): boolean {
    return (
      this.activeTab === 'activity' &&
      !this.act.loading &&
      this.act.rows.length > 0 &&
      this.act.pagination.page < (this.act.pagination.total_pages || 0)
    );
  }

  reload(): void {
    switch (this.activeTab) {
      case 'payments':
        this.loadPayments();
        break;
      case 'refunds':
        this.loadRefunds();
        break;
      case 'activity':
        this.loadActivity();
        break;
      default:
        this.loadAppointments();
    }
  }

  // ═══════════════════════════════════════════════════════════
  //  Detail panel
  // ═══════════════════════════════════════════════════════════

  openAppointment(row: any): void {
    this.openDetail(
      'appointment',
      row.appointment_id,
      this.clientHistoryService.getAppointmentDetail(
        this.clientId,
        row.appointment_id
      ),
      'Unable to load appointment details'
    );
  }

  // The detail endpoint takes the order id: it is the only id every payment
  // row carries, since an order that never reached the gateway has no
  // transaction and no razorpay payment id.
  openPayment(row: any): void {
    this.openDetail(
      'payment',
      row.order_id,
      this.clientHistoryService.getPaymentDetail(this.clientId, row.order_id),
      'Unable to load payment details'
    );
  }

  openRefund(row: any): void {
    this.openDetail(
      'refund',
      row.refund_id,
      this.clientHistoryService.getRefundDetail(this.clientId, row.refund_id),
      'Unable to load refund details'
    );
  }

  // Activity has no per-event endpoint: every field the panel shows is
  // already on the row, so the panel reads it straight off.
  openActivity(row: any): void {
    this.detailKind = 'activity';
    this.detailId = row.event_id;
    this.detail = row;
    this.detailLoading = false;
  }

  private openDetail(
    kind: string,
    id: string,
    request: any,
    errorMessage: string
  ): void {
    this.detailKind = kind;
    this.detailId = id;
    this.detail = null;
    this.detailLoading = true;

    request.subscribe({
      next: (resp: any) => {
        this.detail = resp?.['data'] || null;
        this.detailLoading = false;
      },
      error: (err: any) => {
        this.detailLoading = false;
        this.toastr.error(err?.error?.msg || errorMessage);
      }
    });
  }

  closeDetail(): void {
    this.detailKind = null;
    this.detailId = null;
    this.detail = null;
  }

  // True when the settlement block carries anything at all. Without it the
  // card would be a column of dashes, so the panel says so in words instead.
  get hasSettlement(): boolean {
    const settlement = this.detail?.['settlement'];

    if (!settlement) {
      return false;
    }

    return [
      'invoice_code',
      'net_payable',
      'payout_id',
      'razorpay_payout_id',
      'payout_status',
      'status',
      'utr',
      'completed_at'
    ].some(key => settlement[key] !== null && settlement[key] !== undefined);
  }

  isSelected(kind: string, id: string): boolean {
    return this.detailKind === kind && this.detailId === id;
  }

  // From a payment straight to the appointment it paid for.
  viewRelatedAppointment(): void {
    const appointmentId = this.detail?.appointment_id;

    if (!appointmentId) {
      return;
    }

    this.activeTab = 'appointments';

    if (!this.appt.loaded) {
      this.loadAppointments();
    }

    this.openAppointment({ appointment_id: appointmentId });
  }

  // From an activity event to the payment it belongs to.
  viewRelatedPayment(): void {
    const orderId = this.detail?.order_id;

    if (!orderId) {
      return;
    }

    this.activeTab = 'payments';

    if (!this.pay.loaded) {
      this.loadPayments();
    }

    this.openPayment({ order_id: orderId });
  }

  // What the client paid on top of the consultation fee: the platform fee
  // and the tax on it, which the summary already adds up as `amount`.
  get taxesAndFee(): number | null {
    const charges = this.summary?.['consultation_charges'];

    if (!charges) {
      return null;
    }

    return Number(charges['amount'] || 0);
  }

  // ═══════════════════════════════════════════════════════════
  //  Tabs
  // ═══════════════════════════════════════════════════════════

  selectTab(tab: string): void {
    if (this.activeTab === tab) {
      return;
    }

    this.activeTab = tab;
    this.showDateMenu = false;
    this.showFilterMenu = false;
    this.closeDetail();

    if (!this.state.loaded) {
      this.reload();
    }
  }

  backToClients(): void {
    this.router.navigate(['/admin/lexiz-clients']);
  }

  // ═══════════════════════════════════════════════════════════
  //  Filters
  // ═══════════════════════════════════════════════════════════

  toggleDateMenu(event: Event): void {
    event.stopPropagation();

    this.showFilterMenu = false;
    this.showDateMenu = !this.showDateMenu;

    this.state.draftStart = this.state.startDate;
    this.state.draftEnd = this.state.endDate;
  }

  applyDateRange(): void {
    const state = this.state;

    if (state.draftStart && state.draftEnd && state.draftStart > state.draftEnd) {
      this.toastr.warning('The start date must come before the end date');
      return;
    }

    state.startDate = state.draftStart;
    state.endDate = state.draftEnd;
    this.showDateMenu = false;

    this.resetToFirstPage();
  }

  clearDateRange(): void {
    const state = this.state;

    state.startDate = '';
    state.endDate = '';
    state.draftStart = '';
    state.draftEnd = '';
    this.showDateMenu = false;

    this.resetToFirstPage();
  }

  onFilterChange(): void {
    this.resetToFirstPage();
  }

  toggleFilterMenu(event: Event): void {
    event.stopPropagation();

    this.showDateMenu = false;
    this.showFilterMenu = !this.showFilterMenu;

    if (this.activeTab === 'payments') {
      this.pay.draftSearch = this.pay.search;
    } else if (this.activeTab === 'refunds') {
      this.ref.draftRefundType = this.ref.refundType;
      this.ref.draftSearch = this.ref.search;
    } else {
      this.appt.draftMode = this.appt.mode;
      this.appt.draftPaymentStatus = this.appt.paymentStatus;
    }
  }

  applyFilters(): void {
    if (this.activeTab === 'payments') {
      this.pay.search = this.pay.draftSearch;
    } else if (this.activeTab === 'refunds') {
      this.ref.refundType = this.ref.draftRefundType;
      this.ref.search = this.ref.draftSearch;
    } else {
      this.appt.mode = this.appt.draftMode;
      this.appt.paymentStatus = this.appt.draftPaymentStatus;
    }

    this.showFilterMenu = false;
    this.resetToFirstPage();
  }

  resetFilters(): void {
    if (this.activeTab === 'payments') {
      this.pay.search = '';
      this.pay.draftSearch = '';
    } else if (this.activeTab === 'refunds') {
      this.ref.refundType = '';
      this.ref.search = '';
      this.ref.draftRefundType = '';
      this.ref.draftSearch = '';
    } else {
      this.appt.mode = '';
      this.appt.paymentStatus = '';
      this.appt.draftMode = '';
      this.appt.draftPaymentStatus = '';
    }

    this.showFilterMenu = false;
    this.resetToFirstPage();
  }

  get activeFilterCount(): number {
    if (this.activeTab === 'payments') {
      return this.pay.search ? 1 : 0;
    }

    if (this.activeTab === 'refunds') {
      return (this.ref.refundType ? 1 : 0) + (this.ref.search ? 1 : 0);
    }

    return (this.appt.mode ? 1 : 0) + (this.appt.paymentStatus ? 1 : 0);
  }

  // Activity filters entirely through its event-type dropdown, so it has no
  // filter popover.
  get hasFilterMenu(): boolean {
    return this.activeTab !== 'activity';
  }

  get statusOptionsForTab(): any[] {
    switch (this.activeTab) {
      case 'payments':
        return this.orderStatusOptions;
      case 'refunds':
        return this.refundStatusOptions;
      case 'activity':
        return this.eventTypeOptions;
      default:
        return this.statusOptions;
    }
  }

  get statusPlaceholder(): string {
    return this.activeTab === 'activity' ? 'All Events' : 'Status';
  }

  get tabTitle(): string {
    switch (this.activeTab) {
      case 'payments':
        return 'Payment History';
      case 'refunds':
        return 'Refund History';
      case 'activity':
        return 'Activity Timeline';
      default:
        return 'Appointment History';
    }
  }

  get rowNoun(): string {
    switch (this.activeTab) {
      case 'payments':
        return 'payments';
      case 'refunds':
        return 'refunds';
      case 'activity':
        return 'activities';
      default:
        return 'appointments';
    }
  }

  get dateRangeLabel(): string {
    const state = this.state;

    if (!state.startDate && !state.endDate) {
      return 'All dates';
    }

    const from = state.startDate ? this.formatDate(state.startDate) : 'Any';
    const to = state.endDate ? this.formatDate(state.endDate) : 'Any';

    return `${from} - ${to}`;
  }

  private resetToFirstPage(): void {
    this.state.pagination.page = 1;
    this.closeDetail();
    this.reload();
  }

  // ═══════════════════════════════════════════════════════════
  //  Export
  // ═══════════════════════════════════════════════════════════

  // The columns each exportable tab writes out.
  private exportSpec(): any {
    if (this.activeTab === 'refunds') {
      return {
        name: 'refunds',
        params: () => this.refundParams(),
        fetch: (params: any) =>
          this.clientHistoryService.getRefunds(this.clientId, params),
        headers: [
          'Refund Date',
          'Refund ID',
          'Appointment ID',
          'Lawyer',
          'Original Payment',
          'Refund Amount',
          'Currency',
          'Reason',
          'Status'
        ],
        row: (row: any) => [
          this.formatDateTime(row.refund_date),
          row.razorpay_refund_id || row.refund_id,
          row.appointment_id,
          row.lawyer?.name,
          row.original_payment,
          row.amount,
          row.currency,
          row.reason,
          this.statusLabel(row.status)
        ]
      };
    }

    return {
      name: 'payments',
      params: () => this.paymentParams(),
      fetch: (params: any) =>
        this.clientHistoryService.getPayments(this.clientId, params),
      headers: [
        'Payment Date',
        'Payment ID',
        'Appointment ID',
        'Lawyer',
        'Amount',
        'Currency',
        'Method',
        'Status',
        'Razorpay Order ID'
      ],
      row: (row: any) => [
        this.formatDateTime(row.payment_date),
        row.payment_id,
        row.appointment_id,
        row.lawyer?.name,
        row.amount,
        row.currency,
        this.modeLabel(row.method),
        this.statusLabel(row.status),
        row.razorpay_order_id
      ]
    };
  }

  get canExport(): boolean {
    return this.activeTab === 'payments' || this.activeTab === 'refunds';
  }

  // Exports every row the current filters match, not just the page on screen,
  // so the file is walked page by page at the API's maximum size.
  async exportRows(): Promise<void> {
    if (this.exporting || !this.canExport) {
      return;
    }

    const spec = this.exportSpec();

    this.exporting = true;

    try {
      const rows: any[] = [];
      const limit = 100;

      let page = 1;
      let total = 0;

      // Stop on the first empty page as well as on the count, so a page that
      // comes back short can never spin this loop forever.
      while (true) {
        const params = { ...spec.params(), page, limit };

        const resp: any = await firstValueFrom(spec.fetch(params));

        const batch: any[] = resp?.['data'] || [];

        rows.push(...batch);
        total = resp?.['pagination']?.['total'] || 0;

        if (!batch.length || rows.length >= total) {
          break;
        }

        page++;
      }

      if (!rows.length) {
        this.toastr.info('There is nothing to export');
        return;
      }

      this.downloadCsv(spec, rows);
    } catch (err: any) {
      this.toastr.error(
        err?.error?.msg || `Unable to export the ${spec.name}`
      );
    } finally {
      this.exporting = false;
    }
  }

  private downloadCsv(spec: any, rows: any[]): void {
    const lines = [spec.headers.map(this.csvCell).join(',')];

    rows.forEach((row: any) => {
      lines.push(spec.row(row).map(this.csvCell).join(','));
    });

    const blob = new Blob(['\ufeff' + lines.join('\r\n')], {
      type: 'text/csv;charset=utf-8;'
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);

    link.href = url;
    link.download =
      `${spec.name}-${this.client?.coffer_id || this.clientId}-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private csvCell(value: any): string {
    if (value === null || value === undefined) {
      return '""';
    }

    return `"${String(value).replace(/"/g, '""')}"`;
  }

  copy(value: string | null, label: string = 'Value'): void {
    if (!value) {
      return;
    }

    navigator.clipboard?.writeText(value).then(
      () => this.toastr.success(`${label} copied`),
      () => this.toastr.error(`Unable to copy the ${label.toLowerCase()}`)
    );
  }

  // ═══════════════════════════════════════════════════════════
  //  Pagination
  // ═══════════════════════════════════════════════════════════

  getTotalPages(): number {
    return this.state.pagination?.total_pages || 0;
  }

  getPageNumbers(): number[] {
    const total = this.getTotalPages();
    const current = this.state.pagination.page;

    if (total <= 1) {
      return total === 1 ? [1] : [];
    }

    // A five-page window that stays inside the available range.
    let start = Math.max(1, current - 2);
    const end = Math.min(total, start + 4);

    start = Math.max(1, end - 4);

    const pages: number[] = [];

    for (let page = start; page <= end; page++) {
      pages.push(page);
    }

    return pages;
  }

  goToPage(page: number): void {
    if (
      page < 1 ||
      page > this.getTotalPages() ||
      page === this.state.pagination.page
    ) {
      return;
    }

    this.state.pagination.page = page;
    this.closeDetail();
    this.reload();
  }

  goToPrevPage(): void {
    this.goToPage(this.state.pagination.page - 1);
  }

  goToNextPage(): void {
    this.goToPage(this.state.pagination.page + 1);
  }

  changePageSize(limit: any): void {
    this.state.pagination.limit = Number(limit);
    this.state.pagination.page = 1;
    this.closeDetail();
    this.reload();
  }

  getPaginationRange(): string {
    const state = this.state;
    const noun = this.rowNoun;
    const total = state.pagination?.total || 0;

    if (!total) {
      return `No ${noun}`;
    }

    // Activity's Load More appends pages, so count from the first page still
    // on screen rather than from the current one.
    const from = state.firstPage || state.pagination.page;
    const first = (from - 1) * state.pagination.limit + 1;
    const last = Math.min(first + state.rows.length - 1, total);

    return `Showing ${first} to ${last} of ${total} ${noun}`;
  }

  // ═══════════════════════════════════════════════════════════
  //  Display helpers
  // ═══════════════════════════════════════════════════════════

  initials(name: string | null): string {
    if (!name) {
      return '-';
    }

    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.charAt(0) || '';
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';

    return (first + last).toUpperCase();
  }

  formatDate(value: string | null): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return '-';
    }

    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  formatTime(value: string | null): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  formatDateTime(value: string | null): string {
    if (!value) {
      return '-';
    }

    const date = this.formatDate(value);
    const time = this.formatTime(value);

    return time ? `${date} ${time}` : date;
  }

  money(amount: any, currency: string = 'INR'): string {
    if (amount === null || amount === undefined || amount === '') {
      return '-';
    }

    const value = Number(amount);

    if (isNaN(value)) {
      return '-';
    }

    const symbol = currency === 'INR' ? '₹' : `${currency} `;

    return symbol + value.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }

  label(value: string | null, map: any): string {
    if (!value) {
      return '-';
    }

    if (map[value]) {
      return map[value];
    }

    return value
      .split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  statusLabel(status: string | null): string {
    return this.label(status, this.statusLabels);
  }

  modeLabel(mode: string | null): string {
    return this.label(mode, this.modeLabels);
  }

  statusClass(status: string | null): string {
    switch ((status || '').toLowerCase()) {
      case 'completed':
        return 'status-completed';
      case 'ongoing':
        return 'status-ongoing';
      case 'upcoming':
      case 'confirmed':
        return 'status-confirmed';
      case 'cancelled':
        return 'status-cancelled';
      case 'pending_payment':
      case 'cancellation_pending':
        return 'status-pending';
      case 'abandoned':
      case 'no_show':
        return 'status-abandoned';
      default:
        return 'status-default';
    }
  }

  paymentStatusClass(status: string | null): string {
    switch ((status || '').toLowerCase()) {
      case 'paid':
      case 'captured':
      case 'processed':
      case 'settled':
        return 'status-completed';
      case 'pending':
      case 'created':
      case 'initiated':
      case 'processing':
        return 'status-pending';
      case 'failed':
      case 'reversed':
        return 'status-cancelled';
      default:
        return 'status-default';
    }
  }

  modeIcon(mode: string | null): string {
    switch ((mode || '').toLowerCase()) {
      case 'video_call':
      case 'online':
        return 'bi-camera-video';
      case 'phone_call':
        return 'bi-telephone';
      case 'in_person':
        return 'bi-person-workspace';
      default:
        return 'bi-chat-dots';
    }
  }

  timelineIcon(type: string): string {
    const event = type || '';

    if (event.includes('cancel')) {
      return 'bi-x-circle-fill timeline-cancelled';
    }

    if (event.includes('refund')) {
      return 'bi-arrow-counterclockwise timeline-refund';
    }

    return 'bi-check-circle-fill timeline-done';
  }

  trackByAppointment(_index: number, row: any): string {
    return row.appointment_id;
  }

  trackByPayment(_index: number, row: any): string {
    return row.order_id;
  }

  trackByRefund(_index: number, row: any): string {
    return row.refund_id;
  }

  trackByActivity(_index: number, row: any): string {
    return row.event_id;
  }

  // The coloured dot an activity row leads with: `icon` is the glyph, `tone`
  // the class that colours the dot behind it.
  private activityKind(type: string): any {
    const event = type || '';

    if (event.includes('failed')) {
      return { icon: 'bi-exclamation-lg', tone: 'act-failed' };
    }

    if (event.includes('refund')) {
      return { icon: 'bi-arrow-counterclockwise', tone: 'act-refund' };
    }

    if (event.includes('cancel')) {
      return { icon: 'bi-x-lg', tone: 'act-cancelled' };
    }

    if (event === 'payment_initiated') {
      return { icon: 'bi-currency-rupee', tone: 'act-initiated' };
    }

    if (event.includes('booked') || event.includes('confirmed')) {
      return { icon: 'bi-calendar2-week', tone: 'act-booked' };
    }

    return { icon: 'bi-check-lg', tone: 'act-done' };
  }

  activityIcon(type: string): string {
    return this.activityKind(type).icon;
  }

  activityTone(type: string): string {
    return this.activityKind(type).tone;
  }
}
